/**
 * IncidentsFlow — накопительная диаграмма потока ошибок (CFD).
 *
 * Две накопительные кривые: сколько система нашла и сколько команда закрыла.
 * Читаются они не по отдельности, а зазором между собой:
 *
 *   - по вертикали — сколько ошибок ждёт исправления прямо сейчас;
 *   - по горизонтали — сколько времени ошибка в среднем ждала.
 *
 * Отсюда же видно и то, чего не даёт ни одна сводная цифра: успевает ли
 * команда за системой. Параллельные кривые — успевает, расходящиеся — долг
 * растёт, сходящиеся — разбирает накопленное.
 *
 * Подневные значения без накопления здесь не годятся: в них видно, что вчера
 * нашли 40, а сегодня 12, и не видно ничего, ради чего отчёт открывали.
 *
 * Данные — тот же ряд, что стоял за месячными полосами: `new` за день и
 * `fixed` за день.
 *
 * @format
 */

import {useMemo} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {ReferenceArea} from 'recharts';

import {
  Chart,
  PLOT_RIGHT_MARGIN,
  Y_AXIS_WIDTH,
  type ChartRow,
  type ChartSeries,
} from 'ui/components/organisms/Chart';
import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {DAYS, MEASURE, parseIsoDate, plural} from '../ExecutiveReport/helpers';
import {DAY_OFF_LABEL, dayOff, dayOffSpans} from '../ExecutiveReport/workCalendar';
import {incidentFlowMetrics, pickExtremes, type IncidentDay} from './incidentsFlowMetrics';
import {PeriodAxis} from '../ExecutiveReport/components/PeriodAxis';
import {SplitCard, SplitPanel} from '../ExecutiveReport/components/SplitCard';
import {StatTile} from '../ExecutiveReport/components/StatTile';

export interface IncidentsFlowProps {
  /** Подневный ряд за период, от старого дня к новому. */
  days: IncidentDay[];
  /**
   * Сколько ошибок было открыто на начало периода.
   *
   * Без него график ломается там, где команда разбирает старые завалы:
   * закрытий за период оказывается больше, чем находок, и кривая
   * исправленного лезет выше кривой найденного — то есть показывает
   * исправление того, чего никто не находил.
   */
  openingBacklog?: number;
  height?: number;
}

const Caption = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
  maxWidth: MEASURE,
});

/**
 * Заголовок второй панели.
 *
 * Он не повторяет заголовок этажа, а называет смену предмета: выше был объём
 * работы, здесь — очередь. Без подписи вторая панель читается как продолжение
 * первой в другом масштабе, и её собственный вопрос теряется.
 */
const PanelTitle = styled(Box)({
  ...text('label.l'),
  color: surfaceVar('onSurface'),
  margin: 0,
});

/**
 * Слои складываются в стопку, а не накладываются друг на друга.
 *
 * Наложение двух кривых оставляет долг разницей ДВУХ кромок: чтобы его
 * прочитать, глазу надо измерить расстояние между линиями и удержать его в
 * голове, сравнивая с другим местом графика. В стопке долг — это толщина
 * верхнего слоя, то есть уже измеренная величина; сравнивать толщины глаз
 * умеет без усилий. Верхняя граница стопки при этом остаётся «найденным» —
 * та же информация, но без второй заливки на всё поле.
 *
 * Цвета взяты в полную силу и без обводок: в стопке границу между слоями
 * рисует сам стык, а контур поверх плотной заливки только утяжеляет фигуру,
 * превращая полосу в рамку.
 *
 * Лёгкий спад к низу каждого слоя (`shaded`) оставляет самой чистой верхнюю
 * кромку — ту, по которой величина и считывается, — и снимает с двух больших
 * плашек ощущение аппликации.
 */
/**
 * Пометка нерабочего дня. Первой строкой ряда — она объясняет всё
 * остальное в подсказке: в субботу никто ничего не исправлял не потому,
 * что забыли.
 */
const DAY_OFF_SERIES: ChartSeries = {
  id: 'dayOff',
  label: 'День',
  tooltipOnly: true,
  // Тот же нейтральный, которым залит сам день: маркер строки и подложка под
  // курсором — одна краска, и связь между ними не надо угадывать.
  color: 'neutral',
};

const SERIES: ChartSeries[] = [
  DAY_OFF_SERIES,
  {
    id: 'fixed',
    label: 'Исправленные ошибки',
    type: 'area',
    color: 'good',
    fill: 'shaded',
    // Кромка сделанного поднята над красным слоем: это единственная линия, за
    // которой читатель следит во времени — растёт она или встала, — и
    // пропадать под соседом она не должна.
    outline: true,
    outlineColor: 'good-active',
    raiseOutline: true,
    stackId: 'flow',
  },
  {
    id: 'open',
    label: 'Ждут исправления',
    type: 'area',
    color: 'bad',
    // Ровная заливка: полоса долга тонкая, и спад цвета на её высоте не
    // читается как глубина — он читается как грязный край.
    fill: 'solid',
    outline: false,
    stackId: 'flow',
  },
  // Сумма стопки. Не рисуется — верхняя кромка уже её показывает, — но в
  // тултипе нужна: складывать два числа в уме читатель не обязан.
  {id: 'found', label: 'Найдено всего', tooltipOnly: true},
  // Дневная работа. Накопительные кривые её прячут: на них пологий участок
  // и рывок отличаются только наклоном, а читателю нужен ответ «сколько
  // сделали в этот день».
  {id: 'fixedToday', label: 'Исправлено за день', tooltipOnly: true},
  {
    id: 'fixedTodayShare',
    label: 'От ожидавших',
    tooltipOnly: true,
    unit: '%',
    format: shareFormat,
  },
];

/** Доля с одним знаком: «3,7 %» вместо «3,7412…». */
function shareFormat(value: number): string {
  return value.toLocaleString('ru-RU', {maximumFractionDigits: 1});
}

/**
 * Подложка нерабочего дня. Тот же нейтральный токен и та же тусклость, что
 * в календаре правок: читатель узнаёт отметку один раз на весь отчёт.
 */
const DAY_OFF_FILL = `color-mix(in srgb, ${surfaceVar('onSurfaceMuted')} 8%, transparent)`;

/** Нерабочий отрезок в номерах точек ряда. */
interface OffBand {
  from: number;
  to: number;
}

/**
 * Что Recharts передаёт фигуре: прямоугольник всего периода в пикселях.
 *
 * Ось внутрь фигуры не приходит, поэтому шаг между днями считается из этого
 * прямоугольника — по нему и раскладываются клетки дней.
 */
interface OffBackdropProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  bands?: OffBand[];
  /** Сколько точек в ряду: шагов между ними на единицу меньше. */
  points?: number;
}

function OffBackdrop({x = 0, y = 0, width = 0, height = 0, bands = [], points = 0}: OffBackdropProps) {
  const steps = points - 1;
  if (steps <= 0 || width <= 0) return null;
  const step = width / steps;
  const right = x + width;

  return (
    <g>
      {bands.map(band => {
        // Клетка дня — половина промежутка с каждой стороны. По краям период
        // обрезан, и клетка обрезается вместе с ним: заливка за осью читалась
        // бы как продолжение периода, которого нет.
        const from = Math.max(x, x + (band.from - 0.5) * step);
        const to = Math.min(right, x + (band.to + 0.5) * step);
        if (to <= from) return null;
        return (
          <rect
            key={band.from}
            x={from}
            y={y}
            width={to - from}
            height={height}
            fill={DAY_OFF_FILL}
          />
        );
      })}
    </g>
  );
}

const FLOW_SYNC_ID = 'incidents-flow';

/** Сколько экстремумов подписываем и как далеко они должны стоять друг от друга. */
const EXTREME_COUNT = 2;
const EXTREME_MIN_GAP_DAYS = 14;

/**
 * `2026-07-07` → `7.07`: на оси нужен порядок дат, а не полная дата.
 *
 * Здесь и только здесь ISO становится человеческой подписью — единственная
 * точка, где формат представления вообще появляется. Дата разбирается по
 * компонентам, а не через `new Date()`: ISO-дата без времени читается как
 * полночь UTC, и восточнее Гринвича `getDate()` вернул бы предыдущий день.
 */
function axisDate(value: string | number): string {
  const parts = parseIsoDate(String(value));
  if (!parts) return String(value);
  return `${parts.day}.${String(parts.month).padStart(2, '0')}`;
}

/**
 * `2.4` → `2,4 дня`; целое остаётся целым.
 *
 * Дробное число дней всегда берёт форму родительного падежа: «0,5 дня», а не
 * «0,5 день», хотя округление даёт единицу.
 */
function days(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  if (Number.isInteger(rounded)) return `${rounded} ${plural(rounded, DAYS)}`;
  const label = rounded.toLocaleString('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return `${label} ${DAYS[1]}`;
}

/**
 * `99.669` → `99,67%`; целое остаётся целым.
 *
 * Два знака, а не округление до целого: при 36 705 закрытых из 36 826
 * найденных целое даёт «100%» — рядом с плиткой «осталось открытых 17 780»
 * это читается как противоречие. Сотые доли здесь не педантизм, а
 * единственный способ отличить «всё закрыто» от «почти всё».
 */
function percent(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return `${rounded}%`;
  const label = rounded.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${label}%`;
}

export function IncidentsFlow({days: input, openingBacklog = 0, height = 300}: IncidentsFlowProps) {
  const metrics = useMemo(
    () => incidentFlowMetrics(input, openingBacklog),
    [input, openingBacklog],
  );

  // Recharts перемонтирует детей при новой ссылке в пропах, поэтому строки
  // собираются один раз на данные, а не на каждый рендер страницы.
  const data = useMemo<ChartRow[]>(
    () =>
      metrics.points.map(p => {
        const off = dayOff(p.date);
        return {
          date: p.date,
          fixed: p.fixed,
          open: p.open,
          found: p.found,
          fixedToday: p.fixedToday,
          fixedTodayShare: p.fixedTodayShare,
          // Пустое поле строку подсказки не рисует: в рабочий день писать
          // «рабочий день» незачем.
          dayOff: off ? DAY_OFF_LABEL[off] : undefined,
        };
      }),
    [metrics],
  );

  const openData = useMemo<ChartRow[]>(
    () =>
      metrics.points.map(p => {
        const off = dayOff(p.date);
        return {date: p.date, open: p.open, dayOff: off ? DAY_OFF_LABEL[off] : undefined};
      }),
    [metrics],
  );

  /**
   * Подписываются только два пика и две впадины очереди.
   *
   * Столбцов здесь под полторы сотни — подписать каждый значит не подписать
   * ни одного. Пики называют дни, когда очередь была самой длинной, впадины —
   * когда её почти не было; между ними читается всё остальное.
   */
  const openSeries = useMemo<ChartSeries[]>(() => {
    const values = metrics.points.map(p => p.open);
    // Впадины ищутся с оглядкой на уже подписанные вершины: на ровном ряде
    // максимум и минимум совпадают по значению, и без этого одно и то же
    // число подписывалось бы дважды.
    const peaks = pickExtremes(values, 'max', EXTREME_COUNT, EXTREME_MIN_GAP_DAYS);
    const pits = pickExtremes(
      values,
      'min',
      EXTREME_COUNT,
      EXTREME_MIN_GAP_DAYS,
      peaks.map(i => values[i]),
    );
    const marked = new Set([...peaks, ...pits]);

    return [
      DAY_OFF_SERIES,
      {
        id: 'open',
        label: 'Ждут исправления',
        type: 'bar',
        color: 'alert',
        pointLabel: (value, index) =>
          marked.has(index) ? value.toLocaleString('ru-RU') : undefined,
      },
    ];
  }, [metrics]);

  /**
   * Нерабочие дни в номерах точек ряда.
   *
   * Подряд идущие дни склеены в отрезок: суббота и воскресенье, нарисованные
   * двумя полосами, дают между собой волосяной шов, которого в календаре нет.
   */
  const dates = useMemo(() => metrics.points.map(p => p.date), [metrics]);

  const offBands = useMemo(() => {
    const number = new Map(metrics.points.map((point, index) => [point.date, index]));
    return dayOffSpans(metrics.points).map(span => ({
      from: number.get(span.from) ?? 0,
      to: number.get(span.to) ?? 0,
    }));
  }, [metrics]);

  if (!input.length) return null;

  const lag = metrics.avgLagDays;

  /**
   * Все подложки — одной фигурой на весь период.
   *
   * По полосе на выходной было бы прямее, но ось здесь порядковая: она знает
   * только точки-дни, и полоса «от субботы до субботы» получает нулевую
   * ширину — одиночный праздник просто не нарисовался бы. Фигура на весь
   * домен даёт шаг между днями, а с ним и настоящую ширину дня: от середины
   * промежутка слева до середины справа, как клетка в календаре.
   */
  const offBackdrop = (
    <ReferenceArea
      x1={data[0]?.date as string}
      x2={data[data.length - 1]?.date as string}
      shape={(props: OffBackdropProps) => (
        <OffBackdrop {...props} bands={offBands} points={data.length} />
      )}
    />
  );

  return (
    <Stack gap='m'>
      <Chart
        data={data}
        series={SERIES}
        xKey='date'
        xFormat={axisDate}
        yAxis
        yFromZero
        height={height}
        syncId={FLOW_SYNC_ID}
        cursor='band'
        xAxis={false}
        ariaLabel='Накопительный график: найденные и исправленные ошибки по дням'
        background={offBackdrop}
      />

      {/* Очередь в собственном масштабе: горб здесь — это дни, когда находки
          копились быстрее, чем команда успевала их закрывать.

          Столбцы, а не площадь: очередь измеряется каждый день заново, и
          столбец про это честнее — площадь под линией предлагает суммировать
          то, что суммированию не подлежит (вчерашние ожидающие и сегодняшние
          — по большей части одни и те же ошибки). */}
      <Stack gap='xs'>
        <PanelTitle as='h3'>Сколько ошибок ждало исправления</PanelTitle>
        <Chart
          data={openData}
          series={openSeries}
          xKey='date'
          xFormat={axisDate}
          yAxis
          yFromZero
          grid={false}
          height={Math.round(height * 0.55)}
          syncId={FLOW_SYNC_ID}
          cursor='band'
          xAxis={false}
          ariaLabel='Сколько ошибок ждало исправления в каждый день периода'
          background={offBackdrop}
        />

        {/* Одна рейка на обе диаграммы: они синхронны и делят ось. Своя ось
            Recharts под нижней выключена — числами вроде «1.03» она отвечала
            на тот же вопрос, что и эта рейка, только хуже. */}
        <PeriodAxis dates={dates} insetLeft={Y_AXIS_WIDTH} insetRight={PLOT_RIGHT_MARGIN} />
            </Stack>

      <SplitCard reflowAt='md'>
        <SplitPanel padding='m'>
          <StatTile
            bare
            label='Исправлено'
            value={
              metrics.totalFound > 0
                ? percent((metrics.totalFixed / metrics.totalFound) * 100)
                : '—'
            }
            caption={`${metrics.totalFixed.toLocaleString('ru-RU')} из ${metrics.totalFound.toLocaleString('ru-RU')} найденных`}
            tone={metrics.totalFixed >= metrics.totalFound ? 'good' : 'alert'}
            hint='Доля закрытого от найденного за период. Больше ста процентов означает, что команда разобрала и то, что накопилось раньше.'
          />
        </SplitPanel>
        <SplitPanel padding='m'>
          <StatTile
            bare
            label='Ошибка ждёт исправления'
            value={lag === null ? '—' : days(lag)}
            caption='в среднем за период'
            tone={lag !== null && lag > 3 ? 'alert' : 'good'}
            hint='Горизонтальный зазор между кривыми: сколько дней проходит от находки до закрытия. Пока ошибка открыта, она продолжает тратить бюджет.'
          />
        </SplitPanel>
        <SplitPanel padding='m'>
          <StatTile
            bare
            label='Осталось открытых'
            value={metrics.openAtEnd.toLocaleString('ru-RU')}
            caption={
              metrics.backlogClosed > 0
                ? `пик за период — ${metrics.peakOpen.toLocaleString('ru-RU')}, из старого долга закрыто ${metrics.backlogClosed.toLocaleString('ru-RU')}`
                : `пик за период — ${metrics.peakOpen.toLocaleString('ru-RU')}`
            }
            tone={metrics.openAtEnd > 0 ? 'bad' : 'good'}
            hint='Вертикальный зазор между кривыми на конец периода — ошибки, найденные, но ещё не закрытые.'
          />
        </SplitPanel>
      </SplitCard>

      <Caption>
        Вверху всё найденное за период нарастающим итогом: зелёное — уже исправленное, красная
        полоса сверху — то, что ещё ждёт. Чем она тоньше, тем быстрее команда разбирает находки.
        Внизу та же полоса крупным планом, по дням.
      </Caption>
    </Stack>
  );
}
